import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Chip,
  SelectChangeEvent
} from '@mui/material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getUserReports, dismissReport, takeActionOnReport } from '../services/adminApi';

interface UserReport {
  id: string;
  reporter_id: string;
  reported_user_id: string;
  reporter_email: string | null;
  reported_user_email: string | null;
  reporter_name: string | null;
  reported_user_name: string | null;
  reason: string;
  category: string;
  additional_details: string | null;
  report_context: string;
  context_id: string | null;
  status: 'pending' | 'reviewed' | 'dismissed' | 'action_taken';
  admin_notes: string | null;
  created_at: string;
  reviewed_at: string | null;
  reviewed_by: string | null;
}

export default function UserReports() {
  const [statusFilter, setStatusFilter] = useState<string>('pending');
  const [selectedReport, setSelectedReport] = useState<UserReport | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');
  const [actionType, setActionType] = useState<'dismiss' | 'action'>('dismiss');

  const queryClient = useQueryClient();

  // Fetch reports
  const { data, isLoading, error } = useQuery({
    queryKey: ['userReports', statusFilter],
    queryFn: () => getUserReports(statusFilter),
    refetchInterval: 30000, // Refetch every 30 seconds
  });

  // Dismiss report mutation
  const dismissMutation = useMutation({
    mutationFn: ({ id, notes }: { id: string; notes: string }) => dismissReport(id, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userReports'] });
      handleCloseDialog();
    },
  });

  // Take action mutation
  const actionMutation = useMutation({
    mutationFn: ({ id, notes }: { id: string; notes: string }) => takeActionOnReport(id, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userReports'] });
      handleCloseDialog();
    },
  });

  const handleOpenDialog = (report: UserReport, type: 'dismiss' | 'action') => {
    setSelectedReport(report);
    setActionType(type);
    setAdminNotes(report.admin_notes || '');
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedReport(null);
    setAdminNotes('');
  };

  const handleSubmit = () => {
    if (!selectedReport) return;

    if (actionType === 'dismiss') {
      dismissMutation.mutate({ id: selectedReport.id, notes: adminNotes });
    } else {
      actionMutation.mutate({ id: selectedReport.id, notes: adminNotes });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'reviewed': return 'info';
      case 'dismissed': return 'default';
      case 'action_taken': return 'error';
      default: return 'default';
    }
  };

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'spam': return 'Spam';
      case 'harassment': return 'Harassment';
      case 'inappropriate_content': return 'Inappropriate Content';
      case 'impersonation': return 'Impersonation';
      case 'other': return 'Other';
      default: return category;
    }
  };

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Error loading user reports. Please try again.
        </Alert>
      </Box>
    );
  }

  const reports = data || [];

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        User Reports
      </Typography>

      <Box sx={{ mb: 3 }}>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Status Filter</InputLabel>
          <Select
            value={statusFilter}
            label="Status Filter"
            onChange={(e: SelectChangeEvent) => setStatusFilter(e.target.value)}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="reviewed">Reviewed</MenuItem>
            <MenuItem value="dismissed">Dismissed</MenuItem>
            <MenuItem value="action_taken">Action Taken</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : reports.length === 0 ? (
        <Alert severity="info">
          No user reports found for the selected filter.
        </Alert>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {reports.map((report: UserReport) => (
            <Box
              key={report.id}
              sx={{
                p: 2,
                border: '1px solid #e0e0e0',
                borderRadius: 2,
                backgroundColor: '#fff',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box>
                  <Typography variant="h6" gutterBottom>
                    {getCategoryLabel(report.category)}
                  </Typography>
                  <Chip 
                    label={report.status.replace('_', ' ').toUpperCase()} 
                    color={getStatusColor(report.status)} 
                    size="small"
                    sx={{ mr: 1 }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    Reported {new Date(report.created_at).toLocaleString()}
                  </Typography>
                </Box>
                {report.status === 'pending' && (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      onClick={() => handleOpenDialog(report, 'dismiss')}
                    >
                      Dismiss
                    </Button>
                    <Button
                      variant="contained"
                      color="error"
                      size="small"
                      onClick={() => handleOpenDialog(report, 'action')}
                    >
                      Take Action
                    </Button>
                  </Box>
                )}
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Reporter</Typography>
                  <Typography variant="body2">
                    {report.reporter_name || 'Unknown'} ({report.reporter_id.substring(0, 8)}...)
                  </Typography>
                  {report.reporter_email && (
                    <Typography variant="caption" color="text.secondary">
                      {report.reporter_email}
                    </Typography>
                  )}
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Reported User</Typography>
                  <Typography variant="body2">
                    {report.reported_user_name || 'Unknown'} ({report.reported_user_id.substring(0, 8)}...)
                  </Typography>
                  {report.reported_user_email && (
                    <Typography variant="caption" color="text.secondary">
                      {report.reported_user_email}
                    </Typography>
                  )}
                </Box>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary">Reason</Typography>
                <Typography variant="body2">{report.reason}</Typography>
              </Box>

              {report.additional_details && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary">Additional Details</Typography>
                  <Typography variant="body2">{report.additional_details}</Typography>
                </Box>
              )}

              {report.admin_notes && (
                <Box sx={{ mt: 2, p: 1, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary">Admin Notes</Typography>
                  <Typography variant="body2">{report.admin_notes}</Typography>
                </Box>
              )}

              <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip label={`Context: ${report.report_context}`} size="small" variant="outlined" />
                {report.context_id && (
                  <Chip label={`ID: ${report.context_id.substring(0, 8)}...`} size="small" variant="outlined" />
                )}
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {/* Action Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {actionType === 'dismiss' ? 'Dismiss Report' : 'Take Action on Report'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {actionType === 'dismiss' 
              ? 'Add notes explaining why this report is being dismissed.'
              : 'Add notes describing the action taken on this report.'}
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Admin Notes"
            value={adminNotes}
            onChange={(e) => setAdminNotes(e.target.value)}
            placeholder="Enter notes here..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit}
            variant="contained"
            color={actionType === 'dismiss' ? 'primary' : 'error'}
            disabled={dismissMutation.isPending || actionMutation.isPending}
          >
            {(dismissMutation.isPending || actionMutation.isPending) ? (
              <CircularProgress size={24} />
            ) : (
              actionType === 'dismiss' ? 'Dismiss' : 'Take Action'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
